import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Otp from '../../components/step-2/otp';
import Mobile from '../../components/step-2/mobile';
import AddTickets from '../../components/step-2/addTickets';
import { validateMobile } from '../../components/step-2/validation';
import {notify} from 'app/components';
import { SUB_STEP_ALIAS, FIRST_STEP_PROGRESS } from './constants';
import { generateUniqueId } from '../step-3/utils';
import { createBusinessConfig, getPriceBreakup, resendOtp, sendOtp, updateBusinessConfig, verifyOtp } from '../../api';
import { OTP_LENGTH } from '../../../../constants/common';

const Step2 = (props) => {
  const {
    updateProgress,
    updateStep,
    updateSubStep,
    subStep,
    saveEvent,
  } = props;

  const { eventData: formData, savedEventData, businessConfig = {}, priceBreakups } = useSelector((state) => state.eventCreate);
  const { userData } = useSelector((state) => state.app);

  const [currentScreen, setCurrentScreen] = useState('mobile'); // currentScreen is a string that determines which screen is currently being displayed
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const uniqIdentifier = useRef(null);

  const dispatch = useDispatch();

  // onClickBack is a function that is called when the user clicks the back button
  useEffect(() => {
    window.scrollTo(0, 0);
    updateProgress((prev) => prev + 6.6);
  }, []);

  useEffect(() => {
    if (subStep?.source === 'next') {
      if (subStep?.step == 1) {
        if (userData?.business_config_id) {
          setCurrentScreen('addTickets');
          updateProgress(FIRST_STEP_PROGRESS + 33.33);
        } else if (userData?.isPhoneVerified) {
          setCurrentScreen('addTickets');
          updateProgress(FIRST_STEP_PROGRESS + 33.33);
          createBusinessConfig({
            business_config_id: 0,
            phone_number: userData?.mobile,
            email_id: userData?.email,
            event_id: savedEventData?._id,
          });
        } else {
          setCurrentScreen('mobile');
          updateProgress(FIRST_STEP_PROGRESS + 16.6);
        }
      } else {
        const subSteps = SUB_STEP_ALIAS;
        let hasErrors = false;

        if (currentScreen === 'mobile' && !isOtpOpen) {
          if (validateMobile(phoneNumber)) {
            if (!businessConfig?.phone_number) {
              uniqIdentifier.current = generateUniqueId();
              sendOtp(phoneNumber, uniqIdentifier.current, (data) => {
                if (data?.status === 'success') {
                  setIsOtpOpen(true);
                  setErrors(null);
                } else {
                  if ([data?.message].includes('Invalid phone number')) {
                    setErrors({
                      ...errors,
                      mobileError: 'Please enter a valid mobile number',
                    });
                  } else if (data?.message === 'Phone number already linked') {
                    setErrors({
                      ...errors,
                      mobileError: 'The Phone number is already linked to a verified user',
                    });
                  }
                  notify('error', data?.message || 'failed to send OTP!');
                }
              });
              return;
            }
          } else {
            hasErrors = true;
            setErrors({
              ...errors,
              mobileError: 'Please enter a valid mobile number',
            });
          }
        } else if (currentScreen === 'addTickets') {
          updateProgress(FIRST_STEP_PROGRESS + 33.33);

          if (!formData?.smp_detail_entity_set?.length) {
            notify('error', 'Please add atleast one category to proceed!', { duration: 2000, position: 'bottom-center' });
            hasErrors = true;
          } else {
            updateStep(3);
          }
        }

        if (hasErrors) {
          updateSubStep((prev) => ({ step: prev?.step - 1, source: 'error' }));
          return;
        }

        if (subSteps[subStep?.step]) {
          setCurrentScreen(subSteps[subStep?.step]);
          updateProgress(FIRST_STEP_PROGRESS + (16.665 * subStep?.step));
        } else updateStep(3);
      }
    } else if(subStep?.source === 'error') {
      return;
    } else {
      const subSteps = SUB_STEP_ALIAS;

      if (subStep.step === 'final') {
        updateSubStep({ step: subSteps.final, source: 'prev' });
        updateProgress(33.3 * 2);
        return;
      }

      if (subSteps[subStep?.step] && (subStep?.step === 1 && !businessConfig?.phone_number)) {
        setCurrentScreen(subSteps[subStep?.step]);
        updateProgress(FIRST_STEP_PROGRESS + (16.66 * subStep?.step));
      } else updateStep(1);
    }
  }, [subStep]);

  const setFormData = (data) => {
    dispatch({ type: 'EVENT_CREATE:EVENT_DATA:SET', data });
  };

  // onClickNext is a function that is called when the user clicks the next button
  const onClickNext = () => {
    updateSubStep((prev) => ({ step: prev?.step + 1, source: 'next' }));
  };

  //otp cta handlers -confirm
  // handlePrimaryClick is a function that is called when the user clicks the primary button on the OTP screen
  const handlePrimaryClick = (otp) => {
    if (!errors?.otpError && otp?.length === OTP_LENGTH) {
      verifyOtp(
        phoneNumber,
        otp,
        uniqIdentifier.current,
        (data) => {
          if (data?.status === 'success') {
            setIsOtpOpen(false);
            if (!userData?.business_config_id) {
              createBusinessConfig({
                business_config_id: 0,
                phone_number: phoneNumber,
                email_id: userData?.email,
                event_id: savedEventData?._id,
              }, () => {
                setCurrentScreen('addTickets');
                notify('success', 'Successfully verified!',
                  {duration:2000, position:'bottom-center'});
              });
            } else {
              updateBusinessConfig(
                userData?.business_config_id,
                { ...businessConfig, phone_number: phoneNumber },
                () => {
                  setCurrentScreen('addTickets');
                  notify('success', 'Successfully verified!',
                    {duration:2000, position:'bottom-center'});
                },
              );
            }
          } else {
            setErrors({ ...errors, otpError: data?.description?.reason || 'Please enter correct OTP' });
          }
        },
      );
    } else {
      setErrors({ ...errors, otpError: 'Please enter correct OTP' });
    }
  };

  //otp cta handlers -use different mobile number
  // handleSecondaryClick is a function that is called when the user clicks the secondary button on the OTP screen

  const handleSecondaryClick = ({ clear = false }) => {
    if (clear) {
      setPhoneNumber('');
    }
    setErrors({ ...errors, mobileError: '' });
    setIsOtpOpen(false);
    updateSubStep((prev) => ({ step: prev?.step - 1, source: 'prev' }));
  };

  const setTickets = (tickets, callback) => {
    setFormData({ ...formData, smp_detail_entity_set: tickets });
    if (callback) callback();
  }

  const onClickResend = () => {
    resendOtp(uniqIdentifier.current, phoneNumber);
  };

  return (
    <div className="flex flex-col flex-1 overflow-auto"> 
      {currentScreen === 'mobile' && (
        <Mobile
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          onEnterPress={onClickNext}
          errors={errors}
          setErrors={setErrors}
        />
      )}
      {isOtpOpen && (
        <Otp
          formData={formData}
          isOpen={isOtpOpen}
          handlePrimaryClick={handlePrimaryClick}
          handleSecondaryClick={handleSecondaryClick}
          onEnterPress={handlePrimaryClick}
          errors={errors}
          setErrors={setErrors}
          onResend={onClickResend}
        />
      )}
      {currentScreen === 'addTickets' && (   
        <AddTickets
          eventData={formData}
          businessConfig={businessConfig}
          userData={userData}
          tickets={formData?.smp_detail_entity_set}
          setTickets={setTickets}
          allowNameEdit={true}
          saveEvent={saveEvent}
          priceBreakups={priceBreakups}
          getPriceBreakup={getPriceBreakup}
        />     
      )}
      {/* {selectedTicket && (
          <>
            
          </>
        )} */}
      {/* <Notifier/> */}
    </div>
  );
};

export default Step2;
